/**
 * AssetTable — DataTable + TableToolbar wrapper for Step 2.
 *
 * Read-mode in Story 4.C: search + count chip + sortable columns.
 * Add button and per-row actions land in Story 4.D (CRUD), cascade
 * dialog in 4.E. Kept slim so 4.D can drop in the toolbar `actions`
 * slot and the `_actions` column without restructuring.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import {
  ASSET_COLUMNS_READ,
  ASSET_COLUMNS_EDIT,
  ASSET_ACTIONS_COLUMN_WIDTH,
  type AssetRecord,
} from './assetFieldSchema';
import styles from './AssetTable.module.css';

// DataTable + TableToolbar are JS files; their TS-inferred prop
// shapes mark every prop as required. Cast through `any` here so
// TS doesn't demand `onRowClick`, `onSelectionChange`, etc. at
// every call site. (Long-term fix: add d.ts shims.)
const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export interface AssetTableProps {
  assets: AssetRecord[];
  loading?: boolean;
  emptyMessage?: string;
  /** When false, hides Add button + per-row actions menu + Import. */
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (asset: AssetRecord) => void;
  onDelete?: (asset: AssetRecord) => void;
  /** Export the current filtered/sorted asset list to CSV. */
  onExport?: () => void;
  /** Receive a parsed import file (CSV or JSON) for bulk-add. */
  onImport?: (file: File) => void;
}

function sortAssets(rows: AssetRecord[], sort: SortState | null): AssetRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchAssets(rows: AssetRecord[], query: string): AssetRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function AssetTable({
  assets,
  loading,
  emptyMessage = 'No assets identified yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: AssetTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [typeValues, setTypeValues] = useState<string[]>([]);
  const [propertyValues, setPropertyValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Filter options + counts derived from the live asset list so the
  // drawer reflects whatever asset_type / security_properties the agent
  // produced (not a hard-coded enum).
  const typeOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of assets) {
      const k = String(a.asset_type ?? '').trim();
      if (k) counts[k] = (counts[k] ?? 0) + 1;
    }
    return Object.keys(counts).sort().map((v) => ({ value: v, label: v, count: counts[v] }));
  }, [assets]);

  const propertyOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of assets) {
      if (Array.isArray(a.security_properties)) {
        for (const p of a.security_properties) {
          const k = String(p).trim();
          if (k) counts[k] = (counts[k] ?? 0) + 1;
        }
      }
    }
    return Object.keys(counts).sort().map((v) => ({ value: v, label: v, count: counts[v] }));
  }, [assets]);

  // Pipeline: type (single-value match) → security property (array
  // membership: keep a row if it has ANY selected property) → search → sort.
  const typeFiltered = useMemo(
    () => (typeValues.length === 0
      ? assets
      : assets.filter((a) => typeValues.includes(String(a.asset_type ?? '')))),
    [assets, typeValues],
  );
  const propertyFiltered = useMemo(
    () => (propertyValues.length === 0
      ? typeFiltered
      : typeFiltered.filter((a) =>
          Array.isArray(a.security_properties) &&
          a.security_properties.some((p) => propertyValues.includes(String(p))),
        )),
    [typeFiltered, propertyValues],
  );
  const filtered = useMemo(() => searchAssets(propertyFiltered, search), [propertyFiltered, search]);
  const sorted = useMemo(() => sortAssets(filtered, sort), [filtered, sort]);

  const filters = [
    {
      key: 'asset_type',
      label: 'Type',
      values: typeValues,
      onChange: (next: string[]) => { setTypeValues(next); setPage(1); },
      options: typeOptions,
    },
    {
      key: 'security_properties',
      label: 'Security Property',
      values: propertyValues,
      onChange: (next: string[]) => { setPropertyValues(next); setPage(1); },
      options: propertyOptions,
    },
  ];

  // Slice down to the current page. Clamp page when the filtered
  // set shrinks below the current page index (e.g. search narrows).
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return ASSET_COLUMNS_READ;
    return [
      ...ASSET_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: ASSET_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: AssetRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.asset_name || row.asset_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              // Reset so re-importing the same filename still triggers
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Asset
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
      />
      <DataTableAny
        columns={columns}
        data={paged}
        rowKey="asset_id"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: 'Search assets…',
            }}
            count={{ value: sorted.length, label: 'Assets' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || typeValues.length > 0 || propertyValues.length > 0}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
